import { Target, LineChart, Lock } from 'lucide-react';
import { resolveObjectives } from 'helpers/objectives';
import { OBJECTIVES_LOCKED_MESSAGE, objectivesLocked } from 'helpers/objectives-identity';
import { objectiveOptionGroups } from '@/state/experiments/objective-options';
import { useExperiment } from '@/state/experiments/useExperiment';
import { useObjectiveMapping } from '@/state/experiments/useObjectiveMapping';
import { useSavedObjectives } from '@/state/experiments/useSavedObjectives';
import { useWebsiteDetails } from '@/state/useWebsiteDetails';
import { MappingPrompt } from './Objectives/MappingPrompt';
import { ObjectiveRow } from './Objectives/ObjectiveRow';
import { OBJECTIVES_HELP } from './Objectives/objectives-help';
import { PrimaryObjectivePicker } from './Objectives/PrimaryObjectivePicker';
import { SecondaryObjectivesPicker } from './Objectives/SecondaryObjectivesPicker';
import { ObjectivesSourceLine, SavedObjectives } from './SavedObjectives';
import { HelpTip } from './TargetingHelp';

type GoalsConfigProps = { experimentId: number };

/**
 * What this experiment measures, chosen from the events its website sends.
 *
 * Previously this card returned null unless the website had a Google Analytics property,
 * and sourced its options from GA property tags, so a Pertento-tracked website that
 * reported its own events through pertentoReportConversion could not pick an objective at
 * all. Options now come from the website's own event registry, which is populated by the
 * tracking server from whatever the site actually sends, GA or not.
 *
 * Both pickers offer every event (see objective-options): the website's mapping says what is
 * usually primary, but the focus varies by experiment. Picking an event the website has not
 * mapped saves it straight away, then asks whether to map it on the website too.
 *
 * Laid out as a two-column form (design option A, PER-168): each objective a row, its title
 * and meaning on the left, its control on the right.
 */
export const GoalsConfig = ({ experimentId }: GoalsConfigProps) => {
  const { experiment, updateExperiment } = useExperiment(experimentId);
  const { eventGoals } = useWebsiteDetails(experiment?.websiteId ?? 0);
  const savedObjectives = useSavedObjectives(experimentId);
  const mapping = useObjectiveMapping(experiment?.websiteId ?? 0);

  if (!experiment) return null;

  const goals = (eventGoals ?? []).map(({ name, role }) => ({ name, role }));
  const { primary, secondaries } = resolveObjectives(experiment);
  // What "Website default" would measure, named in the picker even while another event is chosen.
  const websiteDefault = resolveObjectives({ ...experiment, primaryGoal: null });

  const primaryGroups = objectiveOptionGroups({
    goals,
    selected: experiment.primaryGoal ? [experiment.primaryGoal] : [],
  });
  const secondaryGroups = objectiveOptionGroups({ goals, selected: secondaries });
  const hasEvents = goals.length > 0;
  const isLocked = objectivesLocked(experiment.status);

  /**
   * The client already reports a failed request (the API_ERROR dialog); catching here keeps a
   * refusal, such as the 409 when the experiment started in another tab, from being an unhandled
   * rejection, and skips the mapping offer for a goal that was never saved.
   */
  const save = async (payload: Record<string, unknown>): Promise<boolean> => {
    try {
      await updateExperiment(experiment.id, payload);
      return true;
    } catch (error) {
      console.error('Could not save the objectives', error);
      return false;
    }
  };

  const savePrimary = async (next: string | null) => {
    if ((await save({ primaryGoal: next })) && next) mapping.offer({ name: next, role: 'primary' });
  };

  const saveSecondaries = (next: string[]) => save({ secondaryGoals: next });

  return (
    <section className="bg-panel border-line rounded-panel overflow-hidden border" data-tour="settings-objectives">
      <div className="border-line flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3.5">
        <div className="flex min-w-0 flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <h2 className="micro-label text-foreground">Objectives</h2>
            <HelpTip text={OBJECTIVES_HELP.overview} label="objectives" />
          </div>
          <ObjectivesSourceLine manager={savedObjectives} />
        </div>
        <SavedObjectives manager={savedObjectives} />
      </div>

      {isLocked ? (
        <p className="supporting bg-panel2 border-line m-0 flex items-center gap-2 border-b px-5 py-2.5">
          <Lock className="size-3.5 shrink-0" strokeWidth={1.75} aria-hidden="true" />
          {OBJECTIVES_LOCKED_MESSAGE}
        </p>
      ) : null}

      <ObjectiveRow
        icon={<Target className="text-accent size-4" strokeWidth={1.75} aria-hidden="true" />}
        title="Primary objective"
        help={OBJECTIVES_HELP.primary}
        description="Decides the result. The winner and its significance are computed on this event."
      >
        {!hasEvents && !experiment.primaryGoal ? (
          <p className="supporting m-0">
            This website has not sent any events yet. Once it does, choose the one this experiment is judged on here.
          </p>
        ) : (
          <>
            <PrimaryObjectivePicker
              value={experiment.primaryGoal ?? null}
              defaultEvent={websiteDefault.primaryIsDefault ? null : websiteDefault.primary}
              groups={primaryGroups}
              onChange={(next) => void savePrimary(next)}
              disabled={isLocked}
            />
            <MappingPrompt manager={mapping} role="primary" />
          </>
        )}
      </ObjectiveRow>

      <ObjectiveRow
        icon={<LineChart className="text-dim size-4" strokeWidth={1.75} aria-hidden="true" />}
        title="Secondary objectives"
        count={secondaries.length}
        help={OBJECTIVES_HELP.secondary}
        description="Reported next to the primary, in this order. They never change the verdict."
      >
        {!hasEvents && secondaries.length === 0 ? (
          <p className="supporting m-0">This website has not sent any events yet.</p>
        ) : (
          <>
            <SecondaryObjectivesPicker
              selected={secondaries}
              primary={primary}
              readOnly={isLocked}
              groups={secondaryGroups}
              onChange={saveSecondaries}
              onAdded={(name) => mapping.offer({ name, role: 'secondary' })}
            />
            <MappingPrompt manager={mapping} role="secondary" />
          </>
        )}
      </ObjectiveRow>
    </section>
  );
};
